import { useState, useEffect, useCallback } from "react";
import {
    Megaphone, Plus, RefreshCw, Loader2, CheckCircle, AlertTriangle,
    Trash2, Link2, History, FolderTree, Search, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import {
    META_ADS_ACCOUNTS_URL, META_ADS_DISCOVER_URL, META_ADS_CAMPAIGNS_URL,
    META_ADS_SYNC_URL, META_ADS_SYNC_RUNS_URL, PROJECTS_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import MetaConversions from "./MetaConversions";

const STATUS_STYLES = {
    connected:    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    error:        "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    disconnected: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const RUN_STYLES = {
    success: "text-emerald-600 dark:text-emerald-400",
    partial: "text-amber-600 dark:text-amber-400",
    failed:  "text-red-600 dark:text-red-400",
    running: "text-blue-600 dark:text-blue-400",
};

const Section = ({ icon: Icon, title, subtitle, children, action }) => (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Icon className="h-4 w-4" />
                </div>
                <div>
                    <h2 className="text-sm font-semibold text-foreground">{title}</h2>
                    {subtitle && (
                        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                    )}
                </div>
            </div>
            {action}
        </div>
        {children}
    </div>
);

export default function MetaAdsIntegration() {
    const { fetch, create, update, remove } = useApiMethods();

    const [accounts, setAccounts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);

    // Connect flow
    const [token, setToken] = useState("");
    const [discovered, setDiscovered] = useState(null);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [discovering, setDiscovering] = useState(false);
    const [connectingId, setConnectingId] = useState(null);

    // Campaign → project mapping
    const [campaigns, setCampaigns] = useState([]);
    const [campaignSearch, setCampaignSearch] = useState("");
    const [selected, setSelected] = useState([]);
    const [mapProjectId, setMapProjectId] = useState("");
    const [mapping, setMapping] = useState(false);
    const [showMapper, setShowMapper] = useState(false);

    const [syncingId, setSyncingId] = useState(null);

    const loadAccounts = useCallback(() => {
        setLoading(true);
        fetch(META_ADS_ACCOUNTS_URL)
            .then(res => {
                if (res?.status === "success") setAccounts(res.data || []);
                else ToastMsg("error", res?.message || "Failed to load ad accounts");
            })
            .catch(() => ToastMsg("error", "Failed to load ad accounts"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadRuns = useCallback(() => {
        fetch(`${META_ADS_SYNC_RUNS_URL}?limit=10`)
            .then(res => { if (res?.status === "success") setRuns(res.data || []); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCampaigns = useCallback(() => {
        fetch(META_ADS_CAMPAIGNS_URL)
            .then(res => { if (res?.status === "success") setCampaigns(res.data || []); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadAccounts();
        loadRuns();
        loadCampaigns();
        fetch(PROJECTS_URL)
            .then(res => { if (res?.status === "success") setProjects(res.data || []); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── connect ──────────────────────────────────────────────────────────────

    const handleDiscover = () => {
        if (!token.trim()) return ToastMsg("error", "Paste a system user access token first");
        setDiscovering(true);
        create(META_ADS_DISCOVER_URL, { access_token: token.trim() })
            .then(res => {
                if (res?.status === "success") {
                    setDiscovered(res.data || []);
                    setTokenInfo(res.token || null);
                    if (!(res.data || []).length) {
                        ToastMsg("error", "That token can't read any ad accounts");
                    }
                } else {
                    ToastMsg("error", res?.message || "Meta rejected the token");
                }
            })
            .catch(err => ToastMsg("error",
                err?.response?.data?.message || "Could not reach Meta"))
            .finally(() => setDiscovering(false));
    };

    const handleConnect = (account) => {
        setConnectingId(account.account_id);
        create(META_ADS_ACCOUNTS_URL, {
            access_token: token.trim(),
            account_id: account.account_id,
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message);
                    loadAccounts();
                    loadCampaigns();
                } else {
                    ToastMsg("error", res?.message || "Failed to connect");
                }
            })
            .catch(err => ToastMsg("error",
                err?.response?.data?.message || "Failed to connect"))
            .finally(() => setConnectingId(null));
    };

    // ── per-account actions ──────────────────────────────────────────────────

    const patchAccount = (id, payload, successMsg) => {
        update(META_ADS_ACCOUNTS_URL, { id, ...payload })
            .then(res => {
                if (res?.status === "success") {
                    if (successMsg) ToastMsg("success", successMsg);
                    loadAccounts();
                } else {
                    ToastMsg("error", res?.message || "Update failed");
                }
            })
            .catch(() => ToastMsg("error", "Update failed"));
    };

    const handleSync = (account, backfill = false) => {
        setSyncingId(account.id);
        create(META_ADS_SYNC_URL, { account_id: account.id, backfill })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message);
                    // An inline sync has already finished by the time this
                    // resolves; a queued one has not, so the numbers below only
                    // move after the worker writes them.
                    loadAccounts();
                    loadRuns();
                    loadCampaigns();
                } else {
                    ToastMsg("error", res?.message || "Sync failed");
                }
            })
            .catch(() => ToastMsg("error", "Sync failed"))
            .finally(() => setSyncingId(null));
    };

    const handleDisconnect = (account) => {
        remove(META_ADS_ACCOUNTS_URL, { id: account.id })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message);
                    loadAccounts();
                } else {
                    ToastMsg("error", res?.message || "Failed to disconnect");
                }
            })
            .catch(() => ToastMsg("error", "Failed to disconnect"));
    };

    // ── campaign mapping ─────────────────────────────────────────────────────

    const filteredCampaigns = campaigns.filter(c =>
        !campaignSearch || (c.name || "").toLowerCase().includes(campaignSearch.toLowerCase())
    );

    const toggleCampaign = (id) => setSelected(prev =>
        prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );

    const handleMap = () => {
        if (!selected.length) return ToastMsg("error", "Pick at least one campaign");
        setMapping(true);
        update(META_ADS_CAMPAIGNS_URL, {
            campaign_ids: selected,
            project_id: mapProjectId || null,
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message);
                    setSelected([]);
                    loadCampaigns();
                } else {
                    ToastMsg("error", res?.message || "Mapping failed");
                }
            })
            .catch(() => ToastMsg("error", "Mapping failed"))
            .finally(() => setMapping(false));
    };

    const unmappedCount = campaigns.filter(c => !c.project_id && !c.inherited_project_id).length;

    return (
        <ContainerCard>
            <div className="p-2 space-y-5">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Meta Ads</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Connect a Meta ad account to pull spend, campaigns and creatives into
                        the CRM, so cost per lead can be measured against what leads actually did.
                    </p>
                </div>

                {/* ── Connect ─────────────────────────────────────────────── */}
                <Section
                    icon={Link2}
                    title="Connect an ad account"
                    subtitle="Paste a Meta system user token with ads_read. It is encrypted before it is stored and never shown again in full."
                >
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                            type="password"
                            placeholder="EAAxxxxxxxx… system user access token"
                            value={token}
                            onChange={e => setToken(e.target.value)}
                            className="flex-1 font-mono text-xs"
                        />
                        <Button onClick={handleDiscover} disabled={discovering}>
                            {discovering
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <Search className="h-4 w-4 mr-2" />}
                            Find accounts
                        </Button>
                    </div>

                    {tokenInfo && (
                        <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                <span className="text-muted-foreground">
                                    Token: <span className="text-foreground font-medium">{tokenInfo.type}</span>
                                    {tokenInfo.app && <> via <span className="text-foreground">{tokenInfo.app}</span></>}
                                </span>
                                <span className={tokenInfo.never_expires
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : "text-amber-600 dark:text-amber-400"}>
                                    {tokenInfo.never_expires ? "Never expires" : "Expires — will need rotating"}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {(tokenInfo.scopes || []).map(scope => (
                                    <span key={scope}
                                        className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-background border border-border text-muted-foreground">
                                        {scope}
                                    </span>
                                ))}
                            </div>
                            {/* Without leads_retrieval the CRM cannot resolve a leadgen_id to
                                its ad, so lead-level attribution has to arrive on the webhook. */}
                            <div className={cn(
                                "flex items-start gap-2 text-xs rounded-md p-2",
                                tokenInfo.can_read_leads
                                    ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300"
                                    : "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300",
                            )}>
                                {tokenInfo.can_read_leads
                                    ? <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    : <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                                <span>
                                    {tokenInfo.can_read_leads
                                        ? "This token can read lead forms, so historical leads can be back-attributed to their ad."
                                        : "No leads_retrieval scope. Spend reporting works fully; linking each CRM lead to its ad relies on Zapier sending campaign_id, adset_id and ad_id."}
                                </span>
                            </div>
                        </div>
                    )}

                    {discovered && discovered.length > 0 && (
                        <div className="space-y-2">
                            {discovered.map(acc => (
                                <div key={acc.account_id}
                                    className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {acc.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground font-mono">
                                            act_{acc.account_id} · {acc.currency}
                                            {acc.business_name && ` · ${acc.business_name}`}
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant={acc.already_connected ? "outline" : "default"}
                                        disabled={connectingId === acc.account_id}
                                        onClick={() => handleConnect(acc)}
                                    >
                                        {connectingId === acc.account_id
                                            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                            : <Plus className="h-3.5 w-3.5 mr-1.5" />}
                                        {acc.already_connected ? "Reconnect" : "Connect"}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Section>

                {/* ── Connected accounts ──────────────────────────────────── */}
                <Section
                    icon={Megaphone}
                    title="Connected accounts"
                    subtitle={`${accounts.length} account${accounts.length === 1 ? "" : "s"} syncing into this workspace`}
                    action={
                        <Button size="sm" variant="outline" onClick={loadAccounts} disabled={loading}>
                            <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", loading && "animate-spin")} />
                            Refresh
                        </Button>
                    }
                >
                    {loading ? (
                        <div className="flex items-center justify-center py-8 text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                    ) : accounts.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                            No ad account connected yet. Paste a token above to get started.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {accounts.map(acc => (
                                <div key={acc.id} className="rounded-lg border border-border p-4 space-y-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-semibold text-foreground">{acc.name}</p>
                                                <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium",
                                                    STATUS_STYLES[acc.status] || STATUS_STYLES.disconnected)}>
                                                    {acc.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground font-mono mt-0.5">
                                                act_{acc.account_id} · {acc.currency} · token {acc.token_preview || "—"}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" variant="outline"
                                                disabled={syncingId === acc.id}
                                                onClick={() => handleSync(acc, false)}>
                                                {syncingId === acc.id
                                                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                                    : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                                                Sync now
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                disabled={syncingId === acc.id}
                                                onClick={() => handleSync(acc, true)}
                                                title="Pull the last 12 months in chunks">
                                                <History className="h-3.5 w-3.5 mr-1.5" />
                                                Backfill 1y
                                            </Button>
                                            <Button size="sm" variant="ghost"
                                                className="text-red-600 hover:text-red-700"
                                                onClick={() => handleDisconnect(acc)}
                                                title="Disconnect — synced history is kept">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                        <div>
                                            <p className="text-muted-foreground">Campaigns</p>
                                            <p className="text-foreground font-semibold">{acc.campaign_count}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Ads</p>
                                            <p className="text-foreground font-semibold">{acc.ad_count}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Last synced</p>
                                            <p className="text-foreground font-semibold">{acc.last_synced_at || "Never"}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Last run</p>
                                            <p className={cn("font-semibold",
                                                RUN_STYLES[acc.last_run?.status] || "text-muted-foreground")}>
                                                {acc.last_run
                                                    ? `${acc.last_run.status} · ${acc.last_run.insight_rows} rows`
                                                    : "—"}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-4 pt-1">
                                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                            Default project
                                            <select
                                                value={acc.project_id || ""}
                                                onChange={e => patchAccount(acc.id,
                                                    { project_id: e.target.value || null },
                                                    "Project updated")}
                                                className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                                            >
                                                <option value="">Unassigned</option>
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                            Re-sync window
                                            <select
                                                value={acc.sync_window_days}
                                                onChange={e => patchAccount(acc.id,
                                                    { sync_window_days: e.target.value },
                                                    "Sync window updated")}
                                                className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                                            >
                                                {[3, 7, 14, 30].map(d => (
                                                    <option key={d} value={d}>{d} days</option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={acc.auto_sync}
                                                onChange={e => patchAccount(acc.id,
                                                    { auto_sync: e.target.checked })}
                                                className="rounded border-input"
                                            />
                                            Sync nightly
                                        </label>

                                        <label
                                            className="flex items-center gap-2 text-xs text-muted-foreground"
                                            title="Added to Meta's pre-tax spend and budgets in every report"
                                        >
                                            GST on spend
                                            <input
                                                key={`gst-${acc.id}-${acc.gst_percent}`}
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                defaultValue={acc.gst_percent}
                                                onBlur={e => {
                                                    const value = e.target.value === "" ? 0 : Number(e.target.value);
                                                    if (value === Number(acc.gst_percent)) return;
                                                    patchAccount(acc.id, { gst_percent: value }, "GST updated");
                                                }}
                                                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                                                className="h-8 w-20 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                                            />
                                            %
                                        </label>
                                    </div>

                                    {acc.last_error && (
                                        <div className="flex items-start gap-2 text-xs rounded-md p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                            <span className="break-all">{acc.last_error}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Section>

                {/* ── Campaign → project mapping ──────────────────────────── */}
                {campaigns.length > 0 && (
                    <Section
                        icon={FolderTree}
                        title="Campaign to project mapping"
                        subtitle={unmappedCount > 0
                            ? `${unmappedCount} campaign${unmappedCount === 1 ? "" : "s"} not attached to any project — their spend is invisible to project-scoped reports.`
                            : "Every campaign rolls up to a project."}
                        action={
                            <Button size="sm" variant="outline" onClick={() => setShowMapper(s => !s)}>
                                {showMapper ? "Hide" : "Map campaigns"}
                            </Button>
                        }
                    >
                        {showMapper && (
                            <div className="space-y-3">
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Input
                                        placeholder="Search campaigns…"
                                        value={campaignSearch}
                                        onChange={e => setCampaignSearch(e.target.value)}
                                        className="flex-1"
                                    />
                                    <select
                                        value={mapProjectId}
                                        onChange={e => setMapProjectId(e.target.value)}
                                        className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground"
                                    >
                                        <option value="">Inherit from ad account</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                    <Button onClick={handleMap} disabled={mapping || !selected.length}>
                                        {mapping && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                        Apply to {selected.length || 0}
                                    </Button>
                                </div>

                                <div className="max-h-80 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                                    {filteredCampaigns.map(c => (
                                        <label key={c.campaign_id}
                                            className="flex items-center gap-3 p-2.5 hover:bg-accent/50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(c.campaign_id)}
                                                onChange={() => toggleCampaign(c.campaign_id)}
                                                className="rounded border-input shrink-0"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-medium text-foreground truncate">{c.name}</p>
                                                <p className="text-[10px] text-muted-foreground">
                                                    {c.objective} · {c.effective_status} · {c.account_name}
                                                </p>
                                            </div>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                                                {c.project_name
                                                    || (c.inherited_project_id ? "inherited" : "unassigned")}
                                            </span>
                                        </label>
                                    ))}
                                    {filteredCampaigns.length === 0 && (
                                        <p className="p-4 text-xs text-muted-foreground text-center">
                                            No campaigns match that search.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </Section>
                )}

                {/* Outcomes flowing the other way: CRM -> Meta */}
                <MetaConversions />

                {/* ── Sync history ────────────────────────────────────────── */}
                {runs.length > 0 && (
                    <Section icon={History} title="Recent syncs"
                        subtitle="What each run pulled, and how much of Meta's rate limit it used.">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-2 pr-3 font-medium">Started</th>
                                        <th className="py-2 pr-3 font-medium">Account</th>
                                        <th className="py-2 pr-3 font-medium">Trigger</th>
                                        <th className="py-2 pr-3 font-medium">Range</th>
                                        <th className="py-2 pr-3 font-medium text-right">Campaigns</th>
                                        <th className="py-2 pr-3 font-medium text-right">Ads</th>
                                        <th className="py-2 pr-3 font-medium text-right">Rows</th>
                                        <th className="py-2 pr-3 font-medium text-right">API</th>
                                        <th className="py-2 pr-3 font-medium text-right">Took</th>
                                        <th className="py-2 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {runs.map(run => (
                                        <tr key={run.id}>
                                            <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{run.started_at}</td>
                                            <td className="py-2 pr-3 text-foreground truncate max-w-[140px]">{run.account_name}</td>
                                            <td className="py-2 pr-3 text-muted-foreground">{run.trigger}</td>
                                            <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                                                {run.date_from} → {run.date_to}
                                            </td>
                                            <td className="py-2 pr-3 text-right text-foreground">{run.campaigns_synced}</td>
                                            <td className="py-2 pr-3 text-right text-foreground">{run.ads_synced}</td>
                                            <td className="py-2 pr-3 text-right text-foreground">{run.insight_rows}</td>
                                            <td className="py-2 pr-3 text-right text-muted-foreground">{run.api_calls}</td>
                                            <td className="py-2 pr-3 text-right text-muted-foreground">
                                                {run.duration_seconds != null ? `${run.duration_seconds}s` : "—"}
                                            </td>
                                            <td className={cn("py-2 font-medium", RUN_STYLES[run.status])}>
                                                <span className="flex items-center gap-1">
                                                    {run.status === "success"
                                                        ? <CheckCircle className="h-3 w-3" />
                                                        : run.status === "running"
                                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                                            : <AlertTriangle className="h-3 w-3" />}
                                                    {run.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                )}
            </div>
        </ContainerCard>
    );
}
